import NextImage from 'next/image';
import { useRouter } from 'next/router';

import React, { MouseEvent, useState } from 'react';
import ImgsViewer from 'react-images-viewer';
import { useQuery } from 'react-query';
import Balancer from 'react-wrap-balancer';

import styled from 'styled-components';

import Star from '@components/common/Star';
import ServiceSelect from '@components/partners/ServiceSelect';
import PartnerReviewComment from '@components/partners/components/PartnerReviewComment';

import { fetchFeedbacks } from '@utils/api';
import { formatDate } from '@utils/date';
import { serviceBookingCode, serviceName } from '@utils/find-name';
import { buildQueryParams } from '@utils/index';

import { Partner } from '@customTypes/Partner';
import { FeedbackResponse, FeedbacksRequestQuery } from '@customTypes/Review';
import { Services } from '@customTypes/Service';

interface Props {
  partner: Partner;
  reviewCount: number;
  services: Services;
  onTop: () => void;
}

const Wrapper = styled.div`
  margin-top: 24px;
  .filters {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    padding-bottom: 24px;

    select.service-select {
      max-width: 160px;

      &.selected {
        max-width: calc(100vw - 48px);
      }
    }
  }
  .reviews {
    .empty {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 50px 0;
      color: #a3a7ae;
      font-size: 15px;
      text-align: center;
    }
    .review {
      border-top: 1px solid #e3e5ea;
      &:last-child {
        border-bottom: 1px solid #e3e5ea;
      }
      .meta {
        display: flex;
        align-items: center;
        color: #a3a7ae;
        font-size: 13px;
        .region {
          margin-left: 8px;
        }
      }
      .service {
        color: #a3a7ae;
        padding: 16px 0;
        font-size: 14px;
        font-weight: 500;
      }
      .content {
        .content-text {
          line-height: 18px;
        }
        .images {
          margin-top: 24px;
          img {
            margin-right: 5px;
          }
        }
      }
      padding: 24px 0;
      font-size: 15px;
      color: #62656a;
    }
  }
  .pagination {
    padding-top: 30px;
    display: flex;
    justify-content: center;
    .prev {
      svg {
        transform: rotate(180deg);
      }
    }
    .prev,
    .next,
    .page {
      cursor: pointer;
    }
    .page {
      padding: 0 10px;
      font-size: 15px;
      font-weight: 500;
      color: #a3a7ae;
      &.active {
        color: #080808;
      }
    }
  }
`;

const PER_PAGE = 5;
type SortValue = '-score' | '-createdAt' | '+score';
const PartnerReviewList: React.FC<Props> = ({ partner, services, onTop }) => {
  const { query } = useRouter();
  const [currentImgIdx, setCurrentImgIdx] = useState(0);
  const [openedIdx, setOpenedIdx] = useState<number | null>(null);
  const [serviceId, setServiceId] = useState<number | undefined>(parseInt(`${query.serviceId}`) || 0);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<SortValue>('-score');

  const partnerSeviceCodes = partner.service_ids.map((id) => serviceBookingCode(id, services));

  const { data: feedbacks, isFetched } = useQuery<FeedbackResponse>(
    `feedbacks-${partner.id}-${page}-${sort}-${serviceId}`,
    () => {
      const serviceCodes =
        serviceId && partner.service_ids.includes(serviceId)
          ? [serviceBookingCode(serviceId, services)]
          : partnerSeviceCodes;

      const requestQueryParams: FeedbacksRequestQuery = {
        page: page + 1,
        'service-codes': serviceCodes.join(','),
        item_count: PER_PAGE,
        sorts: sort,
      };

      const queryString = buildQueryParams(requestQueryParams);

      return fetchFeedbacks(partner.supplier_id, queryString);
    }
  );
  const onClickPage = (e: MouseEvent<HTMLDivElement>, newPage: number) => {
    onTop();
    setPage(Math.max(0, Math.min(newPage, $maxPage)));
  };

  const reviewCount = feedbacks?.meta?.count || 0;
  const minPage = page - (page % 5);
  const $maxPage = Math.max(0, Math.ceil(reviewCount / PER_PAGE) - 1);
  const maxPage = Math.min(minPage + 4, $maxPage);
  const pages: number[] = [];

  for (let i = minPage; i <= maxPage; i++) pages.push(i);

  return (
    <Wrapper>
      <div className="filters">
        <ServiceSelect
          canSelectNone
          serviceId={serviceId}
          serviceIds={partner.service_ids}
          onSelect={(id) => {
            setPage(0);
            setServiceId(id);
          }}
          services={services}
        />
        <select
          className="no-border text-right"
          value={sort}
          onChange={(e) => {
            setPage(0);
            setSort(e.target.value as SortValue);
          }}
        >
          <option value="-createdAt">최신순</option>
          <option value="-score">평점 높은 순</option>
          <option value="+score">평점 낮은 순</option>
        </select>
      </div>
      {!isFetched || !feedbacks ? null : (
        <>
          <div className="reviews">
            {(feedbacks?.rows || []).length === 0 ? (
              <div className="empty">
                등록된 후기가 없습니다.
                <br />
                파트너가 다른 서비스를 제공 중이라면, 필터를 변경해 확인해보세요.
              </div>
            ) : (
              feedbacks?.rows.map((review, idx) => {
                /** 24.3.5 논의 : 아래와 같이, reviews 배열에는 최대 2개의 데이터만 존재한다
                 *    - 구분 기준 : feedback.reviews 배열 중, 'ancestors' 필드
                 *    - customerReview(고객이 작성한 후기) : ancestors 필드가 falsy
                 *    - partnerComment(고객 후기에 대해 파트너가 작성한 대댓글) : (상위 댓글이 있다는 뜻이므로) ancestors 필드가 truthy
                 */
                const customerReview = review.reviews.find((r) => !r.ancestors);
                const partnerComment = review.reviews.find((r) => !!r.ancestors);

                if (!customerReview) {
                  return null;
                }

                return (
                  <div key={idx} className="review">
                    {customerReview.visibility === 'visible' ? (
                      <>
                        <div className="meta">
                          <Star height={16} rating={review.score} />
                          <div className="region">
                            {customerReview.data?.region} ·{' '}
                            {formatDate(new Date(customerReview.created_at), 'YYYY년 M월 D일(dd)')}
                          </div>
                        </div>
                        <div className="service">{serviceName(Number(review.service_id), services)} 서비스 이용</div>
                        <div className="content">
                          <Balancer className={'content-text'}>{customerReview.content}</Balancer>
                          {customerReview.images.length ? (
                            <>
                              <div className="images">
                                {customerReview.images.map((thumbnail, imageIdx) => (
                                  <React.Fragment key={imageIdx}>
                                    <NextImage
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                      onPointerDown={(e) => {
                                        setOpenedIdx(idx);
                                        setCurrentImgIdx(imageIdx);
                                        e.stopPropagation();
                                        e.preventDefault();
                                      }}
                                      onPointerUp={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                      }}
                                      src={thumbnail.image}
                                      alt="Image"
                                      width={92}
                                      height={92}
                                    />
                                  </React.Fragment>
                                ))}
                              </div>
                              <ImgsViewer
                                currImg={currentImgIdx}
                                backdropCloseable
                                imgs={customerReview.images.map((image) => ({
                                  src: image.image.replace('/thumbnails/', '/images/'),
                                }))}
                                isOpen={openedIdx === idx}
                                showThumbnails
                                onClickPrev={() => setCurrentImgIdx(Math.max(0, currentImgIdx - 1))}
                                onClickNext={() =>
                                  setCurrentImgIdx(Math.min(customerReview.images.length ?? 1 - 1, currentImgIdx + 1))
                                }
                                onClickThumbnail={(i: number) => setCurrentImgIdx(i)}
                                onClose={() => setOpenedIdx(null)}
                              />
                            </>
                          ) : null}
                        </div>
                        <PartnerReviewComment partner={partner} partnerReview={partnerComment} />
                      </>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>

          {$maxPage > 0 ? (
            <div className="pagination">
              {page > 0 ? (
                <div className="prev" onClick={(e) => onClickPage(e, page - 1)}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 17.5L14 12.0505L9 6.5" stroke="#A3A7AE" strokeWidth="2" />
                  </svg>
                </div>
              ) : null}
              {pages.map((p) => (
                <div key={p} className={`page ${page === p ? 'active' : ''}`} onClick={(e) => onClickPage(e, p)}>
                  {p + 1}
                </div>
              ))}
              {page < $maxPage ? (
                <div className="next" onClick={(e) => onClickPage(e, page + 1)}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 17.5L14 12.0505L9 6.5" stroke="#A3A7AE" strokeWidth="2" />
                  </svg>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </Wrapper>
  );
};

export default PartnerReviewList;
